import { useId, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import ConsoleTabs, { panelProps } from '../../components/ConsoleTabs.jsx';
import PageHeader from '../../components/PageHeader.jsx';
import PageState from '../../components/PageState.jsx';
import Toggle from '../../components/Toggle.jsx';
import { LIVE_REASON_TEXT, liveStatusLine } from '../../lib/live-status.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { useNotificationsData } from '../../lib/use-notifications-data.js';
import { notificationsPath } from '../../lib/watch-format.js';
import DeliveryTab from './DeliveryTab.jsx';
import MonitoredAccounts from './MonitoredAccounts.jsx';
import NotificationRecipients from './NotificationRecipients.jsx';
import NotificationRules from './NotificationRules.jsx';
import NotificationsSendLog from './NotificationsSendLog.jsx';

// The Notifications section: who is told about what. The classic daily collection rules and onGuard Watch are both
// notifications, so they share one master switch and one set of tabs. What happened (the Watch events and the flagged
// cases) moved to Activity (ENTRAID-347 plan C3); their old URLs redirect there (pages/CustomerSections.jsx).
// ENTRAID-347 redesign (mockup 5, plan C4): the master switch is the page header's action, and Delivery (the sending
// domain and the ticket integrations, moved from Company Settings) is a tab. Operators (the account flag, not the
// view-as claim) see Rules, Recipients and Delivery read-only, as Company Settings was for them; never the send log,
// and the portal refuses them every Watch content route anyway.
const ADMIN_TABS = [
  { id: 'rules', label: 'Rules', sub: 'rules' },
  { id: 'recipients', label: 'Recipients', sub: 'recipients' },
  { id: 'monitored', label: 'Monitored accounts', sub: 'monitored' },
  { id: 'delivery', label: 'Delivery', sub: 'delivery' },
  { id: 'send-log', label: 'Send log', sub: 'send-log' },
];
// Operators also see Monitored accounts: the portal answers read-only (or refuses Watch content) for them.
const OPERATOR_TABS = ADMIN_TABS.filter((t) => t.id !== 'send-log');

const MESSAGE_RULE = Object.freeze({ ok: 'var(--good)', warn: 'var(--warn)', error: 'var(--bad)' });

// `isOperator` is the navigation claim; `operatorAccount` is the signed-in user's own operator flag
// (watchOperatorAccount): it alone decides what Watch shows or fetches. `notice` (the lifecycle notice) shows under the
// page header.
export default function NotificationsSection({ customerId, config, isOperator, operatorAccount = isOperator, onConfigSaved, notice = null }) {
  const data = useNotificationsData(customerId, { canWatch: !operatorAccount });
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const statusId = useId();

  const tabs = operatorAccount ? OPERATOR_TABS : ADMIN_TABS;
  const base = notificationsPath(customerId);
  const first = location.pathname.startsWith(base) ? location.pathname.slice(base.length).replace(/^\/+/, '').split('/')[0] : '';
  const active = tabs.find((t) => t.sub === first) ?? tabs[0];
  const masterOn = data.classic?.notificationsEnabled === true;

  async function onMaster(next) {
    setBusy(true);
    setMessage(null);
    try {
      const out = await data.setMaster(next);
      const reason = out?.live?.reason;
      setMessage(reason ? { tone: 'warn', text: LIVE_REASON_TEXT[reason] ?? 'Saved.' } : { tone: 'ok', text: next ? 'Notifications are on.' : 'Notifications are off.' });
    } catch (e) {
      setMessage({ tone: 'error', text: e.message });
    } finally {
      setBusy(false);
    }
  }

  if (data.loading) {
    return (
      <div>
        <PageHeader title="Notifications" subline="Who is told about what, and how it is delivered." />
        <PageState state="loading" header={false} label="Loading notifications" />
      </div>
    );
  }
  if (data.errors.classic) {
    return (
      <div>
        <PageHeader title="Notifications" subline="Who is told about what, and how it is delivered." />
        <PageState state="error" title="Notifications could not be loaded" message={data.errors.classic} onRetry={data.reload} />
      </div>
    );
  }

  const pageProps = { customerId, data, config, operatorAccount, onChanged: data.reload };
  const status = liveStatusLine({ masterOn, watch: data.watch });

  return (
    <div>
      <PageHeader
        title="Notifications"
        subline={<span id={statusId}>{status}{masterOn ? '' : ' Turning this on also starts Live watch if at least one live rule is on.'}</span>}
        action={<Toggle label="Notifications" checked={masterOn} disabled={busy} onChange={onMaster} describedBy={statusId} className="font-semibold text-ipc-primary" />}
      />
      <div className="space-y-4">
        {notice}
        {message ? (
          <p role="status" className="rounded-xl border border-ipc-border bg-white px-4 py-3 text-sm text-ipc-ink" style={{ borderLeft: `4px solid ${MESSAGE_RULE[message.tone] ?? MESSAGE_RULE.ok}` }} data-master-message={message.tone}>
            {message.text}
          </p>
        ) : null}
        <ConsoleTabs
          label="Notifications"
          idPrefix="notifications"
          items={tabs.map((t) => ({ id: t.id, label: t.label }))}
          value={active.id}
          onChange={(id) => navigate(notificationsPath(customerId, tabs.find((t) => t.id === id).sub))}
        />
        <div {...panelProps('notifications', active.id)}>
          <Routes>
            <Route index element={<Navigate to={notificationsPath(customerId, 'rules')} replace />} />
            <Route path="rules" element={<NotificationRules {...pageProps} />} />
            <Route path="recipients" element={<NotificationRecipients {...pageProps} onConfigSaved={onConfigSaved} />} />
            <Route path="monitored" element={<MonitoredAccounts customerId={customerId} />} />
            <Route path="delivery" element={<DeliveryTab customerId={customerId} readOnly={operatorAccount} />} />
            {operatorAccount ? null : <Route path="send-log" element={<NotificationsSendLog customerId={customerId} data={data} />} />}
            {operatorAccount ? null : <Route path="guardians" element={<Navigate to={customerSectionPath(customerId, 'people')} replace />} />}
            <Route path="*" element={<Navigate to={notificationsPath(customerId, 'rules')} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}
